import React, { lazy, Suspense, useState } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import SideNav from "./components/hostComponents/SideNav";
import ErrorBoundary from "./components/ErrorBoundary";
import BookDemoModal from "./components/BookDemoModal";
// import ConciergeTrialBanner from "./components/concierge/ConciergeTrialBanner";
import {
  LoginScreen,
  SignupScreen,
  HostScreen,
  AddPropertyScreen,
  ProfileSettingScreen,
  PropertyDetailsScreen,
  PropertiesScreen,
  IntegrationsScreen,
  HostServices,
  PleaseVerifyEmailScreen,
  VerificationScreen,
  ForgetPasswordScreen,
  ChangePasswordScreen,
  Admin,
  DriverStripeRedirectScreen,
  HospitableCallbackScreen,
} from "./screens";
import HostDashboard from "./screens/HostDashboard";
import SupportScreen from "./screens/SupportScreen";
import HostOnboardingScreen from "./screens/HostOnboardingScreen";
import DriverEmailVerificationScreen from "./screens/DriverEmailVerificationScreen";
import OrdersScreen from "./screens/OrdersScreen";
import ConciergeSubscriptionScreen from "./screens/ConciergeSubscriptionScreen";
import OrderDetailsScreen from "./screens/OrderDetailsScreen";
import PageNotFoundScreen from "./screens/PageNotFoundScreen";
import PrivateRoutes from "./components/utilityComponents/PrivateRoutes";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import PasswordResetSuccessful from "./screens/PasswordResetSuccessful";
import ChangeEmailScreen from "./screens/ChangeEmailScreen";
import ChangeEmailVerificationScreen from "./screens/ChangeEmailVerificationScreen";
import MessageCentreScreen from "./screens/MessageCentreScreen";
import SyncGuestyProperties from "./screens/SyncGuestyProperties";
import AffiliateSignupScreen from './screens/AffiliateSignupScreen';
import AffiliateLandingPage from "./screens/AffiliateLandingPage";
import PMSLandingPage from "./screens/PMSLandingPage";
import RequireOta from "./utils/requireOta";

// Lazy load heavy screens to reduce initial bundle size and prevent OOM
const BellhoptPlatinumScreen = lazy(() => import("./screens/BellhoptPlatinumScreen"));
const Email = lazy(() => import("./screens/Email")); // Has large SVG imports (18MB+ files)
const ServiceRequestsScreen = lazy(() => import("./screens/ServiceRequestsScreen"));
const ServiceRequestDetailScreen = lazy(() => import("./screens/ServiceRequestDetailScreen"));

// Redirects from old URLs to standardized routes (keeps old links/emails working)
function RedirectToSettings() {
  const { hid } = useParams();
  return <Navigate to={`/settings/${hid}`} replace />;
}
function RedirectToSettingsReset() {
  const { hid } = useParams();
  return <Navigate to={`/settings/${hid}/reset-password`} replace />;
}
function RedirectToSettingsChangeEmail() {
  const { hid } = useParams();
  return <Navigate to={`/settings/${hid}/change-email`} replace />;
}
function RedirectToPropertyDetails() {
  const { pid } = useParams();
  return <Navigate to={`/properties/${pid}`} replace />;
}

const App = () => {

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);
  const { hostInfo } = useSelector((state) => state.host);

  // Layout for authenticated routes with sidebar
  const PrivateLayout = ({ children }) => (
    <div className="min-h-screen bg-white">
      {/* Book Demo top banner */}
      {!demoBannerDismissed && (
        <div className="bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-10">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">Want a walkthrough?</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowDemoModal(true)}
                className="text-sm font-bold underline underline-offset-2 hover:text-gray-300 transition-colors"
              >
                Book a Demo
              </button>
              <button
                onClick={() => setDemoBannerDismissed(true)}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* <ConciergeTrialBanner /> */}

      <div className="flex w-full min-h-screen">
        <SideNav />
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {showDemoModal && (
        <BookDemoModal onClose={() => setShowDemoModal(false)} />
      )}
    </div>
  );

  // Layout for public routes without sidebar
  const PublicLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-50">
      {children}
    </div>
  );

  return (
    <Routes>
      {/* Private routes with sidebar */}
      <Route element={<PrivateRoutes />}>
        <Route path="/host" element={<PrivateLayout><ErrorBoundary><HostScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/onboarding" element={<PrivateLayout><ErrorBoundary><HostOnboardingScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/properties" element={<PrivateLayout><ErrorBoundary><PropertiesScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/properties/:pid" element={<PrivateLayout><ErrorBoundary><PropertyDetailsScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/integrations" element={<PrivateLayout><ErrorBoundary><IntegrationsScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/concierge-subscription" element={<PrivateLayout><ErrorBoundary><ConciergeSubscriptionScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/concierge" element={<Navigate to="/concierge-subscription" replace />} />
        <Route
          path="/service-requests"
          element={
            <PrivateLayout>
              <ErrorBoundary>
                <Suspense fallback={<div />}>
                  <ServiceRequestsScreen />
                </Suspense>
              </ErrorBoundary>
            </PrivateLayout>
          }
        />
        <Route
          path="/service-requests/:requestId"
          element={
            <PrivateLayout>
              <ErrorBoundary>
                <Suspense fallback={<div />}>
                  <ServiceRequestDetailScreen />
                </Suspense>
              </ErrorBoundary>
            </PrivateLayout>
          }
        />
        {/* <Route path="/services" element={<PrivateLayout><ErrorBoundary><HostServices /></ErrorBoundary></PrivateLayout>} /> */}
        <Route path="/add-property" element={<PrivateLayout><ErrorBoundary><AddPropertyScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/settings/:hid" element={<PrivateLayout><ErrorBoundary><ProfileSettingScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/earnings" element={<PrivateLayout><ErrorBoundary><HostDashboard /></ErrorBoundary></PrivateLayout>} />
        <Route path="/sync-guesty-properties" element={<PrivateLayout><ErrorBoundary><SyncGuestyProperties /></ErrorBoundary></PrivateLayout>} />
        <Route path="/order-list" element={<PrivateLayout><ErrorBoundary><OrdersScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/admin" element={<PrivateLayout><ErrorBoundary><Admin /></ErrorBoundary></PrivateLayout>} />
        <Route path="/settings/:hid/reset-password" element={<PrivateLayout><ErrorBoundary><ResetPasswordScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/settings/:hid/change-email" element={<PrivateLayout><ErrorBoundary><ChangeEmailScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/support" element={<PrivateLayout><ErrorBoundary><SupportScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/message-centre" element={<PrivateLayout><ErrorBoundary><MessageCentreScreen /></ErrorBoundary></PrivateLayout>} />
        <Route path="/email" element={<PrivateLayout><ErrorBoundary><Suspense fallback={<div>Loading...</div>}><Email /></Suspense></ErrorBoundary></PrivateLayout>} />
        {/* Redirects from old URLs */}
        <Route path="/profileSetting/:hid" element={<RedirectToSettings />} />
        <Route path="/profileSetting/:hid/reset-password" element={<RedirectToSettingsReset />} />
        <Route path="/profileSetting/:hid/change-email" element={<RedirectToSettingsChangeEmail />} />
        <Route path="/host/propertyDetails/:pid" element={<RedirectToPropertyDetails />} />
        <Route path="/orderList" element={<Navigate to="/order-list" replace />} />
        <Route path="/addProperty" element={<Navigate to="/add-property" replace />} />
        <Route path="/messageCentre" element={<Navigate to="/message-centre" replace />} />
      </Route>

      {/* Public routes without sidebar */}
      <Route path='/' element={<PublicLayout><LoginScreen /></PublicLayout>} />
      {/* <Route path="/platinum" element={<PublicLayout><Suspense fallback={<div>Loading...</div>}><BellhoptPlatinumScreen /></Suspense></PublicLayout>} /> */}
      <Route path="/login" element={<PublicLayout><LoginScreen /></PublicLayout>} />
      <Route path="/signup" element={<PublicLayout><SignupScreen /></PublicLayout>} />
      <Route path="/signup/affiliate" element={<PublicLayout><AffiliateSignupScreen /></PublicLayout>} />
      <Route path="/signup/affiliate/:code" element={<PublicLayout><AffiliateSignupScreen /></PublicLayout>} />
      <Route path="/pms/:pmsName" element={<PublicLayout><PMSLandingPage /></PublicLayout>} />
      <Route path="/please-verify" element={<PublicLayout><PleaseVerifyEmailScreen /></PublicLayout>} />
      <Route path="/verify-email/:token" element={<PublicLayout><VerificationScreen /></PublicLayout>} />
      <Route path="/integrations/hospitable/callback" element={<PublicLayout><HospitableCallbackScreen /></PublicLayout>} />
      <Route
        path="/order-details/:orderId"
        element={
          hostInfo ? (
            <PrivateLayout><ErrorBoundary><OrderDetailsScreen /></ErrorBoundary></PrivateLayout>
          ) : (
            <PublicLayout><ErrorBoundary><OrderDetailsScreen /></ErrorBoundary></PublicLayout>
          )
        }
      />
      <Route path="/login/forgotPassword" element={<PublicLayout><ForgetPasswordScreen /></PublicLayout>} />
      <Route path="/resetPassword/:token" element={<PublicLayout><ChangePasswordScreen host={true} /></PublicLayout>} />
      <Route path="/password-reset/successful" element={<PublicLayout><PasswordResetSuccessful /></PublicLayout>} />
      <Route path="/support" element={<PublicLayout><SupportScreen /></PublicLayout>} />
      <Route path='/update-email-confirmation/:token' element={<PublicLayout><ChangeEmailVerificationScreen /></PublicLayout>} />
      <Route path="/affiliate-info" element={<PublicLayout><AffiliateLandingPage /></PublicLayout>} />

      {/* DRIVER ROUTES */}
      <Route path="/driver/verify-email/:token" element={<PublicLayout><DriverEmailVerificationScreen /></PublicLayout>} />
      <Route path="/driver/resetPassword/:token" element={<PublicLayout><ChangePasswordScreen driver={true} /></PublicLayout>} />
      <Route path="/driver/stripe" element={<PublicLayout><DriverStripeRedirectScreen /></PublicLayout>} />

      {/* Not Found Route */}
      <Route path="*" element={<PublicLayout><PageNotFoundScreen /></PublicLayout>} />
    </Routes>
  );
};

export default App;
